import { useQuery, UseQueryOptions, QueryKey } from '@tanstack/react-query';
import { HttpTypes } from '@/types';
import { FetchError } from '@/lib/api/client';
import { sdk } from '@/lib/api';

const PRODUCT_QUERY_KEY = 'search-list' as const;
const RELATED_PRODUCT_QUERY_KEY = 'related-product-list' as const;

export const productsQueryKeys = {
  list: (params?: HttpTypes.FindParams, locationId?: string) =>
    [PRODUCT_QUERY_KEY, 'list', { params, locationId }] as const,
  search: (params?: HttpTypes.FindParams, locationId?: string) =>
    [PRODUCT_QUERY_KEY, 'search', { params, locationId }] as const,
};
export const relatedProductQueryKeys = {
  all: [RELATED_PRODUCT_QUERY_KEY] as const,
  list: (slug: string, params?: HttpTypes.FindParams, locationId?: string) =>
    [RELATED_PRODUCT_QUERY_KEY, 'list', { slug, params, locationId }] as const,
};

export const useListProduct = (
  queryParams: HttpTypes.FindParams,
  token?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreProductListResponse,
      FetchError,
      HttpTypes.StoreProductListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productsQueryKeys.list(queryParams),
    queryFn: async () => {
      const result = await sdk.site.homepage.retrieveSearchProduct(queryParams, token as string);
      return result;
    },
    enabled: options?.enabled ?? true,
    ...options,
  });

  return { data, ...rest };
};

export const useGetRelatedProduct = (
  query: HttpTypes.FindParams,
  token?: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreProductListResponse,
      FetchError,
      HttpTypes.StoreProductListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: relatedProductQueryKeys.list(query.slug as string, query, locationId),
    queryFn: async () => {
      const result = await sdk.site.homepage.retrieveRelatedProduct(
        query,
        token as string,
        locationId as string
      );
      return result;
    },
    ...options,
  });

  return { data, ...rest };
};

export const useSearchProduct = (
  queryParams: HttpTypes.FindParams,
  token?: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreProductListResponse,
      FetchError,
      HttpTypes.StoreProductListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productsQueryKeys.search(queryParams),
    queryFn: async () => {
      const result = await sdk.site.homepage.retrieveAllProducts(
        queryParams,
        token as string,
        locationId as string
      );
      return result;
    },
    ...options,
  });

  return { data, ...rest };
};
